import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import {
  getAllPermissions,
  getRoleTemplates,
  getRoles,
  updateRolePermissions,
} from '@/api/services/roles.service'
import { getErrorMessage } from '@/lib/getErrorMessage'
import { usePermittedQuery } from '@/hooks/usePermittedQuery'
import type { AssignPermissionsPayload } from '@/types'

export const rolesQueryKeys = {
  all: ['roles'] as const,
  permissionsAll: ['roles', 'permissions', 'all'] as const,
  templates: ['roles', 'templates'] as const,
}

export function useRoles(options?: { enabled?: boolean }) {
  return usePermittedQuery({
    module: 'roles',
    action: 'read',
    queryKey: rolesQueryKeys.all,
    queryFn: getRoles,
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
    enabled: options?.enabled,
  })
}

export function useAllPermissions() {
  return usePermittedQuery({
    module: 'roles',
    action: 'read',
    queryKey: rolesQueryKeys.permissionsAll,
    queryFn: getAllPermissions,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })
}

export function useRoleTemplates() {
  return usePermittedQuery({
    module: 'roles',
    action: 'read',
    queryKey: rolesQueryKeys.templates,
    queryFn: getRoleTemplates,
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
  })
}

export function useUpdateRolePermissions() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ roleId, payload }: { roleId: string; payload: AssignPermissionsPayload }) =>
      updateRolePermissions(roleId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.all })
      // Permission changes affect the current session's gates if own role changed.
      queryClient.invalidateQueries({ queryKey: ['me'] })
      toast.success('Permissions updated')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })
}
